import React, { Suspense, lazy } from 'react';
import { Redirect, Switch, Route, withRouter, RouteComponentProps } from 'react-router-dom';
import { TransitionGroup, CSSTransition } from 'react-transition-group'
import { Auth, toInjector } from './stores'
import { inject, observer } from 'mobx-react';
import HeaderNavigation from './components/HeaderNavigation'
import HeaderNavigationMock from './components/HeaderNavigationMock'
import ScrollToTop from './components/ScrollToTop'
import { sendLog } from './api/hokan'

// Lazy load all pages for code splitting
const CounterPage = lazy(() => import('./pages/CounterPage'))
const ComponentsPage = lazy(() => import('./pages/ComponentsPage'))
const PatientListPage = lazy(() => import('./pages/PatientListPage'))
const PatientPage = lazy(() => import('./pages/PatientPage'))
const ShinkiIraiPage = lazy(() => import('./pages/ShinkiIraiPage'))
const ScorePage = lazy(() => import('./pages/ScorePage'))
const NotYetPage = lazy(() => import('./pages/NotYetPage'))
const DocumentPage = lazy(() => import('./pages/DocumentPage'))
const DocumentListPage = lazy(() => import('./pages/DocumentListPage'))
const Home = lazy(() => import('./pages/Home'))
const AuthPage = lazy(() => import('./pages/AuthPage'))
const LoginPage = lazy(() => import('./pages/LoginPage'))
const LoginCognitoPage = lazy(() => import('./pages/LoginCognitoPage'))
const GuidlinePage = lazy(() => import('./pages/GuidlinePage'))
const CreateProblemPage = lazy(() => import('./pages/CreateProblemPage'))
const CreateProblemsPage = lazy(() => import('./pages/CreateProblemsPage'))
const GoogleCalendarLoginPage = lazy(() => import('./pages/GoogleCalendarLoginPage'))
const GoogleCalendarRedirectPage = lazy(() => import('./pages/GoogleCalendarRedirectPage'))
const CreateRecordPage = lazy(() => import('./pages/CreateRecordPage'))
const ManagePage = lazy(() => import('./pages/ManagePage'))
const CallPage = lazy(() => import('./pages/CallPage'))
const KangoKirokuPage = lazy(() => import('./pages/KangoKirokuPage'))
const CompanyListPage = lazy(() => import('./pages/CompanyListPage'))
const CompanyPage = lazy(() => import('./pages/CompanyPage'))
const BranchPage = lazy(() => import('./pages/BranchPage'))
const BranchListPage = lazy(() => import('./pages/BranchListPage'))
const UserPage = lazy(() => import('./pages/UserPage'))
const UserListPage = lazy(() => import('./pages/UserListPage'))
const OrganizationListPage = lazy(() => import('./pages/OrganizationListPage'))
const OrganizationPage = lazy(() => import('./pages/OrganizationPage'))
const ImportPage = lazy(() => import('./pages/ImportPage'))
const ExportPage = lazy(() => import('./pages/ExportPage'))
const GenogramPage = lazy(() => import('./pages/GenogramPage'))
const InquiryPage = lazy(() => import('./pages/InquiryPage'))
const KagiAzukariPage = lazy(() => import('./pages/Forms/KagiAzukariPage'))
const JimuPage = lazy(() => import('./pages/JimuPage'))
const JimuJissekiCalendarPage = lazy(() => import('./pages/JimuJissekiCalendarPage'))
const IncidentListPage = lazy(() => import('./pages/IncidentListPage'))
const IncidentPage = lazy(() => import('./pages/IncidentPage'))
const WorkRecordPage = lazy(() => import('./pages/WorkRecordPage'))
const WisemanStaffPage = lazy(() => import('./pages/WisemanStaffPage'))
const WisemanCustomerPage = lazy(() => import('./pages/WisemanCustomerPage'))
const WisemanJissekiPage = lazy(() => import('./pages/WisemanJissekiPage'))
const WyLJissekiPage = lazy(() => import('./pages/WyLJissekiPage'))
const SukoyakaJissekiPage = lazy(() => import('./pages/SukoyakaJissekiPage'))
const ImportStaffPage = lazy(() => import('./pages/ImportStaffPage'))
const ImportPatientPage = lazy(() => import('./pages/ImportPatientPage'))
const TaskListPage = lazy(() => import('./pages/TaskListPage'))
const ChatPage = lazy(() => import('./pages/ChatPage'))
const MyPage = lazy(() => import('./pages/MyPage'))
const MessageListPage = lazy(() => import('./pages/MessageListPage'))
const MessagePage = lazy(() => import('./pages/MessagePage'))
const MessageCreatePage = lazy(() => import('./pages/MessageCreatePage'))
const PasswordResetPage = lazy(() => import('./pages/PasswordResetPage'))
const SystemPage = lazy(() => import('./pages/SystemPage'))
const KiyakuPage = lazy(() => import('./pages/KiyakuPage'))
const ManageMessagePage = lazy(() => import('./pages/ManageMessagePage'))
const EigyouPage = lazy(() => import('./pages/EigyouPage'))

// Loading fallback component
const PageLoader = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
    <div>読み込み中...</div>
  </div>
)
import StackTrace from "stacktrace-js";

/// test

interface Props extends Auth, RouteComponentProps {
}

@inject(...toInjector(Auth))
@observer
class App extends React.Component<Props> { 
	displayName="App"
  public componentDidMount() {
    /*
    try {
      Notification.requestPermission().then(permission => {
        switch (permission) {
          case 'granted':
            console.log("granted");
            break;
          case 'denied':
            console.log("denied")
            break;
          case 'default':
            console.log("default")
            break;
          default:
            break;
        }
      });
    }
    catch (error) {
      if (error instanceof TypeError) {
        Notification.requestPermission((p) => {
          console.log(p);
        }
        );
      } else {
        console.log(error);
        console.log('This browserdoes not support desktop notification');
      }
    }*/

  }

  componentDidCatch(error, info)
  {
    StackTrace.fromError(error).then(err => {
      sendLog({
        path: window.location.href,
        agent: window.navigator.userAgent,
        err: err,
        error: error.toString(),
        info: info
      })
    });
  }

  public render() {
    const currentkey = this.props.location.pathname || ""

    const { auth, location } = this.props
    const isLoginPage = location.pathname === "/login"
    const isPasswordPage = location.pathname === "/password_reset"
    const needLogin = (!isLoginPage && auth!.isLogin === false)

    return <div>
      {
        needLogin || isLoginPage ?
          <HeaderNavigationMock />
          :
          <HeaderNavigation key="head_1" loginMode={false} />
      }

      <TransitionGroup>
        <CSSTransition key={currentkey} classNames='fade' timeout={100} unmountOnExit={true}>
          <div className="print-area" style={{
            marginTop: "3em",
            top: "0", bottom: "0",
            width: "100%",
            overflowY: "scroll",
            WebkitOverflowScrolling: "touch"
          }}>
            <ScrollToTop>
              <Suspense fallback={<PageLoader />}>
              {
                isPasswordPage ?
                  <Switch location={this.props.location}>
                    <Route exact={true} path="/password_reset" component={PasswordResetPage} />
                  </Switch>
                : needLogin ?
                  <Redirect to={{ pathname: "/login", state: { from: location } }} />
                  :
                  <Switch location={this.props.location}>

                    <Route exact={true} path="/login" component={LoginPage} />
                    <Route exact={true} path="/login_cognito" component={LoginCognitoPage} />
                    <Route exact={true} path="/my" component={MyPage} />
                    <Route exact={true} path="/patients" component={PatientListPage} />
                    <Route exact={true} path="/patients/shinki_irai/:patient_id" component={ShinkiIraiPage} />
                    <Route exact={true} path="/patients/:patient_id" component={PatientPage} />
                    <Route exact={true} path="/patients/:patient_id/kango_kiroku" component={KangoKirokuPage} />
                    <Route exact={true} path="/patients/:patient_id/create_problems" component={CreateProblemsPage} />
                    <Route exact={true} path="/patients/:patient_id/create_problem" component={CreateProblemPage} />
                    <Route exact={true} path="/patients/:patient_id/create_problem/:problem_id" component={CreateProblemPage} />
                    <Route exact={true} path="/patients/:patient_id/create_problem/:problem_id/:ass_id" component={CreateProblemPage} />
                    <Route exact={true} path="/patients/:patient_id/create_record" component={CreateRecordPage} />

                    <Route exact={true} path="/document" component={DocumentListPage} />
                    <Route exact={true} path="/document/genogram" component={GenogramPage} />
                    <Route exact={true} path="/document/:document_id" component={DocumentPage} />
                    <Route exact={true} path="/document/generate/kagi_azukari" component={KagiAzukariPage} />
                    
                    <Route exact={true} path="/jimu" component={JimuPage} />
                    <Route exact={true} path="/jimu/jisseki_calendar" component={JimuJissekiCalendarPage} />

                    <Route exact={true} path="/task" component={TaskListPage} />
                    <Route exact={true} path="/call" component={CallPage} />
                    <Route exact={true} path="/score" component={ScorePage} />
                    <Route exact={true} path="/guidline" component={GuidlinePage} />
                    <Route exact={true} path="/inquiry" component={InquiryPage} />
                    <Route exact={true} path="/incident" component={IncidentListPage} />
                    <Route exact={true} path="/incident/:incident_id" component={IncidentPage} />
                    <Route exact={true} path="/work_record" component={WorkRecordPage} />

                    <Route exact={true} path="/message" component={MessageListPage} />
                    <Route exact={true} path="/message/new" component={MessageCreatePage} />
                    <Route exact={true} path="/message/:message_id" component={MessagePage} />

                    <Route exact={true} path="/manage" component={ManagePage} />
                    <Route exact={true} path="/manage/notyet" component={NotYetPage} />
                    <Route exact={true} path="/manage/company" component={CompanyListPage} />
                    <Route exact={true} path="/manage/company/:company_id" component={CompanyPage} />
                    <Route exact={true} path="/manage/branch" component={BranchListPage} />
                    <Route exact={true} path="/manage/branch/:branch_id" component={BranchPage} />
                    <Route exact={true} path="/manage/user" component={UserListPage} />
                    <Route exact={true} path="/manage/user/:user_id" component={UserPage} />
                    <Route exact={true} path="/manage/user" component={UserPage} />
                    <Route exact={true} path="/manage/organizations" component={OrganizationListPage} />
                    <Route exact={true} path="/manage/organizations/:org_id" component={OrganizationPage} />
                    <Route exact={true} path="/manage/import" component={ImportPage} />
                    <Route exact={true} path="/manage/message" component={ManageMessagePage} />
                    <Route exact={true} path="/manage/import/wiseman_staff" component={WisemanStaffPage} />
                      <Route exact={true} path="/manage/import/wiseman_customer" component={WisemanCustomerPage} />
                      <Route exact={true} path="/manage/import/wiseman_jisseki" component={WisemanJissekiPage} />
                      <Route exact={true} path="/manage/import/wyl_jisseki" component={WyLJissekiPage} />
                      <Route exact={true} path="/manage/import/sukoyaka_jisseki" component={SukoyakaJissekiPage} />
                      <Route exact={true} path="/manage/import/staff" component={ImportStaffPage} />
                      <Route exact={true} path="/manage/import/patient" component={ImportPatientPage} />
                      <Route exact={true} path="/manage/import/eigyou" component={EigyouPage} />
                    <Route exact={true} path="/manage/export" component={ExportPage} />

                    /* system */
                      <Route path="/system" component={SystemPage} />

                    /* dev */
                    <Route exact={true} path="/chat" component={ChatPage} />
                    <Route exact={true} path="/counter" component={CounterPage} />
                    <Route exact={true} path="/user" component={UserPage} />
                      <Route exact={true} path="/auth" component={AuthPage} />
                      <Route exact={true} path="/components" component={ComponentsPage} />
                      <Route exact={true} path="/terms" component={KiyakuPage} />
                    <Route exact={true} path="/google-calendar-oauth" component={GoogleCalendarLoginPage} />
                    <Route exact={true} path="/google-calendar-oauth/redirect" component={GoogleCalendarRedirectPage} />

                    <Route path="/home" component={Home} />
                    <Route path="/" component={PatientListPage} />
                  </Switch>
              }
              </Suspense>
            </ScrollToTop>
            <div style={{ color: "gray", textAlign: "center", fontSize: "smaller", margin:"10px"}}>
              <img src="/images/icons/fullcolor_logo.png" style={{width:"200px", margin:"5px"}} />
              <div>ⓒ2023 メディカルインフォマティクス株式会社. All Rights Reserved.</div>
              <div>㈳オマハシステムジャパンよりコード提供</div>
            </div>
          </div>
        </CSSTransition>
      </TransitionGroup>
    </div>
  }
}

export default withRouter(App);
